import { Box, CircularProgress, Paper } from '@mui/material';
import { useSelector } from 'react-redux';

import { Dialog, DialogButton } from '@/components/Dialog';
import Loader from '@/components/Loader';
import ExportGroupComponent from '@/pages/DataHub/Export/ExportGroup';
import ExportPreview from '@/pages/DataHub/Export/ExportPreview';
import SuccessScreen from '@/pages/DataHub/Export/SuccessScreen';
import { RootState } from '@/store';

import { exportOptionsGroups } from './exportList';
import { ExportSettingsContext } from './ExportSettingsContext';
import useDataHubExport from './useDataHubExport';

const DataHubExport = () => {
  const {
    exportData,
    loading,
    exported,
    exportError,
    setExportError,
    setExported,
    previewOptionId,
    isExcelPreview,
    setPreviewOptionId,
    exportSettingsMemo
  } = useDataHubExport();
  const { status, dataHubs } = useSelector((state: RootState) => state.dataHub);
  const isDataHubLoading = !dataHubs?.length && status === 'loading';

  if (isDataHubLoading)
    return (
      <Box sx={{ flex: 1 }}>
        <Loader />
      </Box>
    );

  return (
    <ExportSettingsContext.Provider value={exportSettingsMemo}>
      <Box sx={{ display: 'flex', height: 820 }}>
        <Paper sx={{ flexGrow: 1, p: 2, mt: 2, pt: 0, height: 'fit-content' }}>
          <Box sx={{ display: 'flex' }}>
            <Box
              sx={theme => ({
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                width: '65%',
                [theme.breakpoints.down('lg')]: {
                  width: 'initial',
                  minWidth: 280
                }
              })}
            >
              {exportOptionsGroups.map(({ groupName, GroupIcon, options }) => (
                <ExportGroupComponent
                  key={groupName}
                  GroupIcon={GroupIcon}
                  groupName={groupName}
                  options={options}
                  setPreviewOptionId={setPreviewOptionId}
                />
              ))}
            </Box>
            <ExportPreview
              exportData={exportData}
              previewOptionId={previewOptionId}
            />
          </Box>
        </Paper>
        {!isExcelPreview && (
          <Dialog
            open={loading || exported || exportError}
            onClose={() => {
              setExported(false);
              setExportError(false);
              setPreviewOptionId('');
            }}
          >
            <Box sx={{ backgroundColor: 'background.default', p: 4, minWidth: 400 }}>
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress
                    size={20}
                    sx={{ mr: 2 }}
                  />
                  Your request is being processed...
                </Box>
              )}
              {exported && (
                <SuccessScreen
                  setExported={setExported}
                  setPreviewOptionId={setPreviewOptionId}
                />
              )}
              {exportError && (
                <>
                  Thanks! We'll be in touch shortly.
                  <Box sx={{ textAlign: 'right', mt: 3 }}>
                    <DialogButton
                      variant='contained'
                      onClick={() => {
                        setExported(false);
                        setExportError(false);
                        setPreviewOptionId('');
                      }}
                    >
                      Close
                    </DialogButton>
                  </Box>
                </>
              )}
            </Box>
          </Dialog>
        )}
      </Box>
    </ExportSettingsContext.Provider>
  );
};

export default DataHubExport;
